import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import RNFS from 'react-native-fs';
import {
  Camera,
  runAtTargetFps,
  useCameraDevice,
  useCameraPermission,
  useFrameProcessor,
} from 'react-native-vision-camera';
import {
  type Face,
  useFaceDetector,
} from 'react-native-vision-camera-face-detector';
import { Worklets } from 'react-native-worklets-core';
import { AppButton } from './AppButton';
import { FaceGuideCorners } from './FaceGuideCorners';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { deleteTemporaryImage } from '../utils/temporaryImage';
import {
  classifyFaceYaw,
  ENROLLMENT_POSES,
  ENROLLMENT_POSE_HINTS,
  ENROLLMENT_POSE_LABELS,
  ENROLLMENT_POSE_SHORT_LABELS,
  poseGuidanceMessage,
  type FaceEnrollmentPose,
} from '../utils/faceEnrollmentPose';

type Props = {
  guidanceLabel?: string;
  /** Required head pose for this capture. Capture is blocked until it matches. */
  requiredPose?: FaceEnrollmentPose;
  disabled?: boolean;
  /** Start the in-app front camera as soon as the component mounts. */
  autoLaunch?: boolean;
  /**
   * When false (default), a successful capture is submitted immediately.
   * When true, show Retake / Use photo confirmation first.
   */
  confirmBeforeUse?: boolean;
  /** Hide the top copy block when the parent screen already shows instructions. */
  compact?: boolean;
  onCaptured: (uri: string) => void;
  onCancel?: () => void;
};

const STAGE_BG = '#0B1220';
const MIN_FACE_SIZE = 0.18;
const POSE_HOLD_MS = 280;
const FRAME_PROCESSOR_DETACH_MS = 160;

function toFileUri(path: string) {
  if (path.startsWith('file://')) return path;
  return `file://${path}`;
}

async function ensurePrivateCaptureDir(): Promise<string> {
  const dir = `${RNFS.CachesDirectoryPath}/enrollment-faces`;
  if (!(await RNFS.exists(dir))) {
    await RNFS.mkdir(dir);
  }
  return dir;
}

function delay(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}

function hasUsableFace(faces: Face[]): boolean {
  if (!faces.length) return false;
  const face = faces[0];
  return face.bounds.width > 0 && face.bounds.height > 0;
}

/**
 * In-app front camera for face enrollment.
 * Opens the live preview inside the app (no system camera / external app).
 */
export function FaceEnrollmentCamera({
  guidanceLabel,
  requiredPose,
  disabled = false,
  autoLaunch = true,
  confirmBeforeUse = false,
  compact = false,
  onCaptured,
  onCancel,
}: Props) {
  const palette = useAppColors();
  const device = useCameraDevice('front');
  const { hasPermission, requestPermission } = useCameraPermission();
  const cameraRef = useRef<Camera>(null);
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;
  const requiredPoseRef = useRef(requiredPose);
  requiredPoseRef.current = requiredPose;

  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraActive, setCameraActive] = useState(autoLaunch);
  const [frameProcessorEnabled, setFrameProcessorEnabled] = useState(true);
  const [detectedPose, setDetectedPose] = useState<
    FaceEnrollmentPose | 'unknown' | null
  >(null);
  const [poseReady, setPoseReady] = useState(false);
  const poseMatchSinceRef = useRef<number | null>(null);
  const capturingRef = useRef(false);

  const poseLabel =
    requiredPose != null
      ? ENROLLMENT_POSE_LABELS[requiredPose]
      : guidanceLabel ?? 'Front-facing sample';

  const faceDetectionOptions = useRef({
    performanceMode: 'accurate' as const,
    landmarkMode: 'none' as const,
    contourMode: 'none' as const,
    classificationMode: 'none' as const,
    trackingEnabled: false,
    cameraFacing: 'front' as const,
    minFaceSize: MIN_FACE_SIZE,
    autoMode: false,
  }).current;

  const { detectFaces, stopListeners } = useFaceDetector(faceDetectionOptions);

  useEffect(() => {
    return () => {
      stopListeners();
    };
  }, [stopListeners]);

  useEffect(() => {
    return () => {
      if (previewUri) {
        deleteTemporaryImage(previewUri).catch(() => undefined);
      }
    };
  }, [previewUri]);

  useEffect(() => {
    poseMatchSinceRef.current = null;
    setDetectedPose(null);
    setPoseReady(false);
    setError('');
  }, [requiredPose]);

  useEffect(() => {
    if (hasPermission) return;
    let cancelled = false;
    requestPermission().then(granted => {
      if (cancelled) return;
      if (!granted) {
        setError('Camera permission is required to capture a sample.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [hasPermission, requestPermission]);

  useEffect(() => {
    if (!autoLaunch || disabled || previewUri) return;
    setCameraActive(true);
  }, [autoLaunch, disabled, previewUri]);

  const handleFaces = useCallback((faces: Face[]) => {
    const required = requiredPoseRef.current;
    if (!required) return;

    if (!hasUsableFace(faces)) {
      poseMatchSinceRef.current = null;
      setDetectedPose(null);
      setPoseReady(false);
      return;
    }

    const pose = classifyFaceYaw(faces[0].yawAngle);
    setDetectedPose(pose);
    if (pose !== required) {
      poseMatchSinceRef.current = null;
      setPoseReady(false);
      return;
    }

    const now = Date.now();
    if (poseMatchSinceRef.current == null) {
      poseMatchSinceRef.current = now;
    }
    if (now - poseMatchSinceRef.current >= POSE_HOLD_MS) {
      setPoseReady(true);
    }
  }, []);

  const handleFacesOnJS = useMemo(
    () =>
      Worklets.createRunOnJS((faces: Face[]) => {
        handleFaces(faces);
      }),
    [handleFaces],
  );

  const frameProcessor = useFrameProcessor(
    frame => {
      'worklet';
      runAtTargetFps(4, () => {
        'worklet';
        const faces = detectFaces(frame);
        handleFacesOnJS(faces);
      });
    },
    [detectFaces, handleFacesOnJS],
  );

  async function captureSample() {
    if (disabled || busy || capturingRef.current || previewUri) return;

    if (!cameraActive) {
      setError('');
      setCameraActive(true);
      return;
    }

    if (!hasPermission) {
      const granted = await requestPermission();
      if (!granted) {
        setError('Camera permission is required to capture a sample.');
        return;
      }
    }

    if (!cameraRef.current || !cameraReady) {
      setError('Camera is still starting. Try again in a moment.');
      return;
    }

    if (requiredPose && !poseReady) {
      setError(
        detectedPose == null
          ? 'Position your face in the frame first.'
          : ENROLLMENT_POSE_HINTS[requiredPose],
      );
      return;
    }

    capturingRef.current = true;
    setError('');
    setBusy(true);
    // Detach the face-detector worklet before takePhoto. Leaving it attached
    // fails capture on Android (same pattern as kiosk attendance).
    setFrameProcessorEnabled(false);
    try {
      await delay(FRAME_PROCESSOR_DETACH_MS);
      const captureDir = await ensurePrivateCaptureDir();
      if (!cameraRef.current) {
        throw new Error('camera_unavailable');
      }
      const photo = await cameraRef.current.takePhoto({
        flash: 'off',
        enableShutterSound: false,
        path: captureDir,
      });
      const uri = toFileUri(photo.path);
      if (previewUri) await deleteTemporaryImage(previewUri);

      if (confirmBeforeUse) {
        setCameraActive(false);
        setPreviewUri(uri);
        return;
      }

      onCapturedRef.current(uri);
    } catch {
      setError('Camera capture failed. Try again.');
    } finally {
      capturingRef.current = false;
      setBusy(false);
      setFrameProcessorEnabled(true);
    }
  }

  async function retake() {
    if (previewUri) await deleteTemporaryImage(previewUri);
    setPreviewUri(null);
    setError('');
    setCameraReady(false);
    setPoseReady(false);
    setDetectedPose(null);
    poseMatchSinceRef.current = null;
    setCameraActive(true);
  }

  function usePhoto() {
    if (!previewUri || disabled) return;
    const uri = previewUri;
    setPreviewUri(null);
    onCaptured(uri);
  }

  function handleReset() {
    if (busy) return;
    if (previewUri) {
      retake().catch(() => undefined);
      return;
    }
    setError('');
    setCameraReady(false);
    setPoseReady(false);
    setDetectedPose(null);
    poseMatchSinceRef.current = null;
    setCameraActive(true);
    onCancel?.();
  }

  const showLivePreview = cameraActive && !previewUri && hasPermission && !!device;
  const poseLocked = requiredPose != null && !poseReady;
  const useFrameProcessorPipeline =
    Boolean(requiredPose) && frameProcessorEnabled && !disabled && !busy;
  const captureBlocked =
    disabled ||
    busy ||
    (!!device && hasPermission && cameraActive && !cameraReady) ||
    poseLocked;
  const captureTitle = previewUri
    ? 'Use photo'
    : !cameraActive
      ? 'Start camera'
      : busy
        ? 'Capturing…'
        : 'Capture Face';
  const poseStatus =
    requiredPose != null
      ? poseGuidanceMessage(requiredPose, detectedPose)
      : null;
  const guideColor = poseReady
    ? palette.success
    : requiredPose
      ? '#5BA3FF'
      : undefined;

  return (
    <View style={styles.container}>
      {!compact ? (
        <View style={styles.copyBlock}>
          <Text style={[styles.kicker, { color: palette.textMuted }]}>
            In-app front camera
          </Text>
          <Text style={[styles.guidance, { color: palette.textPrimary }]}>
            {poseLabel}
          </Text>
          <Text style={[styles.hint, { color: palette.textSecondary }]}>
            {requiredPose
              ? ENROLLMENT_POSE_HINTS[requiredPose]
              : 'Center your face in the frame, look at the camera, then capture. Capture a clear, well-lit photo for each recommended angle.'}
          </Text>
        </View>
      ) : requiredPose ? (
        <Text style={[styles.guidance, { color: palette.textPrimary }]}>
          {poseLabel}
        </Text>
      ) : null}

      {requiredPose ? (
        <View style={styles.steps} accessibilityLabel="Enrollment pose steps">
          {ENROLLMENT_POSES.map(pose => {
            const active = pose === requiredPose;
            return (
              <View
                key={pose}
                style={[
                  styles.step,
                  {
                    borderColor: active ? palette.primary : palette.border,
                    backgroundColor: active
                      ? `${palette.primary}18`
                      : palette.surface,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.stepText,
                    { color: active ? palette.primary : palette.textMuted },
                  ]}
                >
                  {ENROLLMENT_POSE_SHORT_LABELS[pose]}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}

      <View
        style={[
          styles.stage,
          {
            backgroundColor: STAGE_BG,
            borderColor: palette.border,
          },
        ]}
      >
        <View
          style={styles.frame}
          accessibilityLabel="Face position guide"
        >
          {previewUri ? (
            <Image source={{ uri: previewUri }} style={styles.preview} />
          ) : showLivePreview ? (
            <View style={styles.cameraClip} collapsable={false}>
              <Camera
                ref={cameraRef}
                style={styles.camera}
                device={device}
                isActive={!disabled}
                photo
                isMirrored
                {...(useFrameProcessorPipeline
                  ? {
                      pixelFormat: 'yuv' as const,
                      frameProcessor,
                    }
                  : {})}
                onInitialized={() => setCameraReady(true)}
                accessibilityLabel="Front camera preview"
              />
            </View>
          ) : (
            <View style={styles.placeholder}>
              {!hasPermission ? (
                <>
                  <ActivityIndicator color={palette.white} />
                  <Text style={styles.placeholderText}>
                    Waiting for camera permission…
                  </Text>
                </>
              ) : !device ? (
                <Text style={styles.placeholderText}>
                  Front camera is unavailable on this device.
                </Text>
              ) : (
                <Text style={styles.placeholderText}>
                  Front camera will start here
                </Text>
              )}
            </View>
          )}

          <FaceGuideCorners color={guideColor} />
        </View>

        {showLivePreview && !cameraReady ? (
          <View style={styles.readyOverlay}>
            <ActivityIndicator color={palette.white} />
            <Text style={styles.placeholderText}>Starting front camera…</Text>
          </View>
        ) : null}

        {requiredPose && showLivePreview && cameraReady ? (
          <View
            style={[
              styles.posePill,
              {
                backgroundColor: poseReady
                  ? 'rgba(22,163,74,0.92)'
                  : 'rgba(15,23,42,0.82)',
              },
            ]}
            accessibilityLiveRegion="polite"
            accessibilityRole="text"
            accessibilityLabel={poseStatus ?? undefined}
          >
            <Text style={styles.posePillText}>{poseStatus}</Text>
          </View>
        ) : null}
      </View>

      {error ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {error}
        </Text>
      ) : null}

      {previewUri ? (
        <View style={styles.row}>
          <AppButton
            title="Retake"
            variant="secondary"
            onPress={retake}
            disabled={disabled || busy}
            style={styles.half}
          />
          <AppButton
            title="Use photo"
            onPress={usePhoto}
            disabled={disabled || busy}
            style={styles.half}
          />
        </View>
      ) : (
        <View style={styles.actions}>
          <AppButton
            title={captureTitle}
            onPress={captureSample}
            loading={busy}
            disabled={captureBlocked}
            accessibilityLabel="Capture face enrollment sample with front camera"
          />
          {onCancel ? (
            <AppButton
              title="Reset"
              variant="secondary"
              onPress={handleReset}
              disabled={busy}
              style={{ borderColor: palette.primary }}
              accessibilityLabel="Reset face capture"
            />
          ) : null}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.md },
  copyBlock: { gap: spacing.xs },
  kicker: {
    ...typography.caption,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  guidance: { ...typography.sectionTitle },
  hint: { ...typography.caption },
  steps: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  step: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: spacing.xs,
    alignItems: 'center',
  },
  stepText: {
    ...typography.caption,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  stage: {
    minHeight: 340,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: spacing.md,
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 4,
  },
  frame: {
    width: '100%',
    aspectRatio: 3 / 4,
    maxHeight: 420,
    borderRadius: 14,
    backgroundColor: '#060A12',
    overflow: 'hidden',
  },
  cameraClip: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#060A12',
  },
  camera: {
    width: '100%',
    height: '100%',
  },
  preview: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  placeholderText: {
    ...typography.caption,
    color: 'rgba(248,250,252,0.82)',
    textAlign: 'center',
  },
  readyOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: 'rgba(11,18,32,0.45)',
  },
  posePill: {
    position: 'absolute',
    bottom: spacing.lg,
    left: spacing.lg,
    right: spacing.lg,
    borderRadius: 999,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
  },
  posePillText: {
    ...typography.caption,
    color: '#F8FAFC',
    fontWeight: '600',
    textAlign: 'center',
  },
  actions: { gap: spacing.sm },
  row: { flexDirection: 'row', gap: spacing.sm },
  half: { flex: 1 },
  error: { ...typography.caption },
});
